import { Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { UseFormReturn } from 'react-hook-form';
import { useEffect } from 'react';
import { shuffle } from 'lodash';
import {
  AssessmentCard,
  ControlledDateField,
  ControlledMaskedField,
  ControlledSelectField,
  ControlledTextField,
} from '../shared';
import { rules } from './rules';
import {
  GuardianFormValues,
  getRelationOptions,
} from './guardian-demographics-form';
import { stateOptions } from '../../../helpers/states';
import { selectUser } from '../../../redux/session';
import { selectPatient } from '../../../redux/assessment';
import { selectLdFlags } from '../../../redux/ld-flags';
import { PatientGuardianInput } from '../../../generated/graphql';
import { DISCOVERY_CHANNEL_OPTIONS } from '../../../helpers/const';

type PropTypes = {
  form: UseFormReturn<GuardianFormValues>;
  primaryGuardian?: PatientGuardianInput;
};

export const GuardianDemographicsPrimary = ({
  form,
  primaryGuardian,
}: PropTypes) => {
  const { control, setValue, watch } = form;
  const patient = useSelector(selectPatient);
  const { email: prefilledAuthEmail } = useSelector(selectUser);
  const { moreGuardianRelationOptions } = useSelector(selectLdFlags);
  const relationOptions = getRelationOptions(moreGuardianRelationOptions);
  const isAddressSameAsMinor = watch('isPrimaryGuardianAddressSameAsMinor');

  // The DiscoveryChannel field is combined with DiscoveryChannelOther
  // on submission; retreiving the data requires separating the string
  // into the two fields.
  const loadDiscoveryChannelOptionFromData = (
    guardian: PatientGuardianInput
  ) => {
    if (!guardian?.discoveryChannel) return '';
    if (guardian.discoveryChannel.startsWith('Other: ')) return 'Other';
  };

  const loadDiscoveryChannelOtherFromData = (
    guardian: PatientGuardianInput
  ) => {
    if (!guardian?.discoveryChannel) return '';
    if (guardian.discoveryChannel.startsWith('Other: '))
      return guardian.discoveryChannel.substring(7);
  };

  const handlePrimaryAddressSameAsPatientChange = () => {
    setValue('isPrimaryGuardianAddressSameAsMinor', !isAddressSameAsMinor);
  };

  const populateOrClearAllAddressFields = () => {
    const fieldToValueMap: Partial<Record<keyof GuardianFormValues, string>> = {
      primaryGuardianAddressStreet1: patient.addressStreet1,
      primaryGuardianAddressStreet2: patient.addressStreet2,
      primaryGuardianAddressCity: patient.addressCity,
      primaryGuardianAddressState: patient.addressState,
      primaryGuardianAddressZipCode: patient.addressZipCode,
    };

    const updateAddressValue = (
      name: keyof GuardianFormValues,
      value: string
    ) => {
      setValue(name, isAddressSameAsMinor ? value : '', {
        shouldValidate: !!isAddressSameAsMinor,
      });
    };

    Object.entries(fieldToValueMap).forEach(
      ([key, value]: [keyof GuardianFormValues, string]) => {
        updateAddressValue(key, value);
      }
    );
  };

  useEffect(() => {
    populateOrClearAllAddressFields();
  }, [isAddressSameAsMinor]);

  return (
    <>
      <AssessmentCard>
        <Grid container columnSpacing={1} rowSpacing={2}>
          <Grid item xs={12}>
            <Typography component="h4" variant="h6">
              Information
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledTextField<GuardianFormValues>
              name="primaryGuardianFirstName"
              control={control}
              defaultValue={primaryGuardian?.firstName ?? ''}
              autoComplete="on"
              inputProps={{ maxLength: 50 }}
              label="Legal first name"
              rules={rules.firstName}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledTextField<GuardianFormValues>
              name="primaryGuardianLastName"
              control={control}
              defaultValue={primaryGuardian?.lastName ?? ''}
              autoComplete="on"
              inputProps={{ maxLength: 50 }}
              label="Last name"
              rules={rules.lastName}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledDateField<GuardianFormValues>
              name="primaryGuardianBirthDate"
              form={form}
              defaultValue={primaryGuardian?.birthDate ?? null}
              label="Date of birth"
              rules={rules.birthDate}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledMaskedField<GuardianFormValues>
              name="primaryGuardianPhone"
              control={control}
              defaultValue={primaryGuardian?.phone ?? ''}
              autoComplete="on"
              rules={rules.phone}
              label="Phone"
              mask="(999) 999-9999"
            />
          </Grid>
          <Grid item xs={12}>
            <ControlledTextField<GuardianFormValues>
              name="primaryGuardianEmail"
              control={control}
              defaultValue={prefilledAuthEmail}
              autoComplete="off"
              disabled
              label="Email"
            />
          </Grid>
          <Grid item xs={12}>
            <ControlledSelectField<GuardianFormValues>
              name="primaryGuardianRelationToPatient"
              control={control}
              defaultValue={primaryGuardian?.relationToPatient ?? ''}
              label="Relation to patient"
              rules={rules.relationToPatient}
              options={relationOptions}
            />
          </Grid>
          <Grid item xs={12}>
            <ControlledSelectField<GuardianFormValues>
              name="discoveryChannel"
              control={control}
              defaultValue={loadDiscoveryChannelOptionFromData(primaryGuardian)}
              options={shuffle(DISCOVERY_CHANNEL_OPTIONS).map(option => ({
                label: option,
                value: option,
              }))}
              label="Where did you first hear about us? (optional)"
            />
          </Grid>
          {watch('discoveryChannel') === 'Other' && (
            <Grid item xs={12}>
              <ControlledTextField<GuardianFormValues>
                name="discoveryChannelOther"
                control={control}
                defaultValue={loadDiscoveryChannelOtherFromData(
                  primaryGuardian
                )}
                autoComplete="off"
                label=""
                inputProps={{ maxLength: 500 }}
                placeholder="Please explain"
                showCharCount
              />
            </Grid>
          )}
        </Grid>
      </AssessmentCard>
      <AssessmentCard>
        <Grid container columnSpacing={1} rowSpacing={2}>
          <Grid item xs={12}>
            <Typography component="h4" variant="h6">
              Address
            </Typography>
            <FormControlLabel
              label="Address same as patient"
              control={<Checkbox />}
              checked={isAddressSameAsMinor}
              onChange={handlePrimaryAddressSameAsPatientChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledTextField<GuardianFormValues>
              name="primaryGuardianAddressStreet1"
              control={control}
              defaultValue={isAddressSameAsMinor ? patient.addressStreet1 : ''}
              autoComplete="on"
              inputProps={{ maxLength: 50 }}
              label="Street"
              disabled={isAddressSameAsMinor}
              rules={rules.addressStreet1}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledTextField<GuardianFormValues>
              name="primaryGuardianAddressStreet2"
              control={control}
              defaultValue={isAddressSameAsMinor ? patient.addressStreet2 : ''}
              autoComplete="on"
              inputProps={{ maxLength: 50 }}
              label="Apt, suite, etc. (optional)"
              disabled={isAddressSameAsMinor}
            />
          </Grid>
          <Grid item xs={12}>
            <ControlledTextField<GuardianFormValues>
              name="primaryGuardianAddressCity"
              control={control}
              defaultValue={isAddressSameAsMinor ? patient.addressCity : ''}
              autoComplete="on"
              inputProps={{ maxLength: 50 }}
              label="City"
              disabled={isAddressSameAsMinor}
              rules={rules.addressCity}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledSelectField<GuardianFormValues>
              name="primaryGuardianAddressState"
              control={control}
              defaultValue={isAddressSameAsMinor ? patient.addressState : ''}
              options={stateOptions}
              label="State"
              disabled={isAddressSameAsMinor}
              rules={rules.addressState}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledTextField<GuardianFormValues>
              name="primaryGuardianAddressZipCode"
              control={control}
              defaultValue={isAddressSameAsMinor ? patient.addressZipCode : ''}
              autoComplete="on"
              inputMode="numeric"
              label="ZIP"
              disabled={isAddressSameAsMinor}
              rules={rules.addressZipCode}
            />
          </Grid>
        </Grid>
      </AssessmentCard>
    </>
  );
};
